import AutomationPolicySettings from './reconciliation/AutomationPolicySettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import SettingsModal from '../Settings/SettingsModal';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, Power, ShieldAlert } from 'lucide-react';

import {
    getAutoPostSettings,
    setAutoPostClass,
    setAutoPostKillSwitch,
    eligibilityBadge,
    formatPct,
} from '../../../services/InsightsService';

/**
 * Automatisk bokföring — the learned auto-post governance surface (design
 * §4.4 §3, Phase 4 item 1). A section on Insikter, gated by the 'auto_post'
 * feature by the parent (and again by the endpoint, which 403s when absent).
 *
 * Per transaction class: measured accuracy, an eligibility badge (reused P3
 * eligibilityBadge), an enable toggle that is DISABLED until the class has
 * independently EARNED calibration eligibility (tooltip = the backend reason),
 * and a required amount cap (beloppstak) when enabling. Copy is explicit that
 * auto-post books WITHOUT review up to the cap.
 *
 * A prominent global Nödstopp (kill switch) sits on top — the instant brake
 * that stops every class from auto-posting, confirmed before it changes.
 *
 * Graceful by contract: a 403/404 hides the whole section (the feature/endpoint
 * is absent); any other error shows an inline off-state, never a crash.
 */

const nf = new Intl.NumberFormat('sv-SE');

// A local editable snapshot of one class row (enabled + cap-as-string).
function toEditState(classes = []) {
    const map = {};
    for (const c of classes) {
        map[c.transaction_class] = {
            enabled: !!c.enabled,
            amountCap: c.amount_cap != null ? String(c.amount_cap) : '',
        };
    }
    return map;
}

export default function AutoPostSettingsSection({ companyId }) {
    const [policyRefresh,setPolicyRefresh]=useState(0);
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false); // 403/404 → hide entirely
    const [error, setError] = useState(null);
    const [killSwitch, setKillSwitch] = useState(false);
    const [classes, setClasses] = useState([]);
    const [edits, setEdits] = useState({});
    const [savingClass, setSavingClass] = useState(null);
    const [killConfirm, setKillConfirm] = useState(null); // pending target bool
    const [killBusy, setKillBusy] = useState(false);

    const reload = useCallback(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getAutoPostSettings(companyId)
            .then((data) => {
                if (cancelled) return;
                const rows = Array.isArray(data?.classes) ? data.classes : [];
                setClasses(rows);
                setEdits(toEditState(rows));
                setKillSwitch(!!data?.kill_switch);
                setPolicyRefresh(n=>n+1);
                setHidden(false);
            })
            .catch((err) => {
                if (cancelled) return;
                const status = err?.response?.status;
                if (status === 403 || status === 404) {
                    setHidden(true); // feature/endpoint absent — hide the section
                } else {
                    setError(err?.response?.data?.message || err.message || 'Kunde inte hämta auto-bokföringens inställningar');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [companyId]);

    useEffect(() => reload(), [reload]);

    const baseline = useMemo(() => toEditState(classes), [classes]);

    const isDirty = useCallback((cls) => {
        const a = edits[cls];
        const b = baseline[cls];
        if (!a || !b) return false;
        return a.enabled !== b.enabled || (a.amountCap || '') !== (b.amountCap || '');
    }, [edits, baseline]);

    const setEnabled = (cls, enabled) => {
        setEdits((prev) => ({ ...prev, [cls]: { ...prev[cls], enabled } }));
    };
    const setCap = (cls, amountCap) => {
        setEdits((prev) => ({ ...prev, [cls]: { ...prev[cls], amountCap } }));
    };

    // Enabling a class REQUIRES a positive cap (the beloppstak the auto-post
    // will never exceed). Disabling never needs one.
    const capInvalid = (cls) => {
        const edit = edits[cls];
        if (!edit || !edit.enabled) return false;
        const n = parseFloat(edit.amountCap);
        return !Number.isFinite(n) || n <= 0;
    };

    const handleSaveClass = useCallback(async (cls) => {
        const edit = edits[cls];
        if (!edit) return;
        setSavingClass(cls);
        try {
            await setAutoPostClass(companyId, {
                transactionClass: cls,
                enabled: edit.enabled,
                amountCap: edit.enabled ? edit.amountCap : null,
            });
            setPolicyRefresh(n=>n+1);
            toast.success(edit.enabled
                ? 'Auto-bokföring aktiverad för klassen.'
                : 'Auto-bokföring avaktiverad för klassen.');
            // Fold the saved edit into the baseline so the row stops being dirty.
            setClasses((prev) => prev.map((c) => (
                c.transaction_class === cls
                    ? { ...c, enabled: edit.enabled, amount_cap: edit.enabled ? parseFloat(edit.amountCap) : null }
                    : c
            )));
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte spara klassinställningen');
        } finally {
            setSavingClass(null);
        }
    }, [companyId, edits]);

    const handleKillSwitch = useCallback(async () => {
        const on = killConfirm;
        setKillBusy(true);
        try {
            const data = await setAutoPostKillSwitch(companyId, on);
            setKillSwitch(!!data?.kill_switch);
            setPolicyRefresh(n=>n+1);
            toast.success(on
                ? 'Nödstopp aktiverat — ingen auto-bokföring sker.'
                : 'Nödstopp släppt — aktiverade klasser kan auto-bokföra igen.');
            setKillConfirm(null);
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte ändra nödstoppet');
        } finally {
            setKillBusy(false);
        }
    }, [companyId, killConfirm]);

    if (hidden) return null;

    return (
        <div className="insights-box">
            <AutomationPolicySettings companyId={companyId} refreshKey={policyRefresh} showKillSwitch={false} onSaved={p=>setKillSwitch(p.kill_switch)} />
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Power size={16} style={{ color: '#6b7280' }} />
                    Automatisk bokföring
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                    Aktiverade klasser bokförs <strong>utan granskning</strong> upp till beloppstaket. En klass kan bara
                    aktiveras när den har uppmätt tillräcklig träffsäkerhet (Redo).
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Laddar…
                </div>
            ) : error ? (
                <div style={{ padding: 16 }}>
                    <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: '0.8125rem' }}>
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                </div>
            ) : (
                <>
                    {/* Nödstopp — the prominent global brake. */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                        borderBottom: '1px solid #f3f4f6',
                        background: killSwitch ? '#fef2f2' : '#f9fafb',
                    }}>
                        <ShieldAlert size={20} style={{ color: killSwitch ? '#dc2626' : '#9ca3af', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: killSwitch ? '#b91c1c' : '#1f2937' }}>
                                Nödstopp {killSwitch ? '(aktivt)' : '(av)'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {killSwitch
                                    ? 'Ingen klass auto-bokför just nu — allt landar som förslag att granska.'
                                    : 'Stoppar all auto-bokföring omedelbart, oavsett klassinställningar.'}
                            </div>
                        </div>
                        <Form.Check
                            type="switch"
                            id="autopost-killswitch"
                            checked={killSwitch}
                            onChange={(e) => setKillConfirm(e.target.checked)}
                            label={killSwitch ? 'På' : 'Av'}
                        />
                    </div>

                    {classes.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.8125rem' }}>
                            Inga transaktionsklasser att styra ännu — statistiken byggs upp när förslag besvaras.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table className="mb-0 align-middle" style={{ fontSize: '0.8125rem' }}>
                                <thead>
                                    <tr style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
                                        <th>Transaktionsklass</th>
                                        <th className="text-end">Beslut</th>
                                        <th className="text-end">Acceptans</th>
                                        <th className="text-center">Status</th>
                                        <th>Beloppstak (kr)</th>
                                        <th className="text-center">Auto-bokför</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {classes.map((c) => {
                                        const cls = c.transaction_class;
                                        const edit = edits[cls] || { enabled: false, amountCap: '' };
                                        const badge = eligibilityBadge({ eligible: c.calibration_eligible, reason: c.readiness_reason });
                                        // Can only ENABLE an eligible class; an already-enabled
                                        // (grandfathered) class can always be turned OFF.
                                        const toggleDisabled = savingClass === cls
                                            || (!c.calibration_eligible && !edit.enabled);
                                        const badCap = capInvalid(cls);
                                        const toggle = (
                                            <Form.Check
                                                type="switch"
                                                id={`autopost-${cls}`}
                                                checked={edit.enabled}
                                                disabled={toggleDisabled}
                                                onChange={(e) => setEnabled(cls, e.target.checked)}
                                            />
                                        );
                                        return (
                                            <tr key={cls}>
                                                <td style={{ fontWeight: 500 }}>{cls}</td>
                                                <td className="text-end">{nf.format(c.decisions ?? 0)}</td>
                                                <td className="text-end">{formatPct(c.accept_rate)}</td>
                                                <td className="text-center">
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id={`autopost-reason-${cls}`}>{c.readiness_reason || '–'}</Tooltip>}
                                                    >
                                                        <Badge bg={badge.variant} style={{ fontSize: '0.6875rem', cursor: 'default' }}>
                                                            {badge.label}
                                                        </Badge>
                                                    </OverlayTrigger>
                                                </td>
                                                <td style={{ minWidth: 130 }}>
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        min="0"
                                                        step="100"
                                                        placeholder="Inget tak"
                                                        value={edit.amountCap}
                                                        isInvalid={badCap}
                                                        disabled={savingClass === cls}
                                                        onChange={(e) => setCap(cls, e.target.value)}
                                                        style={{ maxWidth: 130 }}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    {toggleDisabled && !c.calibration_eligible ? (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id={`autopost-gate-${cls}`}>{c.readiness_reason || 'Klassen är inte redo för auto-bokföring ännu.'}</Tooltip>}
                                                        >
                                                            <span className="d-inline-block">{toggle}</span>
                                                        </OverlayTrigger>
                                                    ) : toggle}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        disabled={!isDirty(cls) || badCap || savingClass === cls}
                                                        onClick={() => handleSaveClass(cls)}
                                                    >
                                                        {savingClass === cls ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : 'Spara'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}

            {/* Kill-switch confirm — describes the effect in the pending direction. */}
            <SettingsModal
                show={killConfirm !== null}
                onHide={() => !killBusy && setKillConfirm(null)}
                closeButton={!killBusy}
                icon={AlertTriangle}
                title={killConfirm ? 'Aktivera nödstopp?' : 'Släpp nödstopp?'}
                footer={(
                    <>
                        <Button variant="outline-secondary" onClick={() => setKillConfirm(null)} disabled={killBusy}>
                            Avbryt
                        </Button>
                        <Button variant={killConfirm ? 'danger' : 'warning'} onClick={handleKillSwitch} disabled={killBusy}>
                            {killBusy ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                            {killConfirm ? 'Aktivera nödstopp' : 'Släpp nödstopp'}
                        </Button>
                    </>
                )}
            >
                {killConfirm ? (
                    <p className="mb-0">
                        Nödstoppet stoppar <strong>all</strong> auto-bokföring omedelbart. Aktiverade klasser
                        fortsätter att ge förslag, men inget bokförs utan granskning förrän du släpper stoppet.
                    </p>
                ) : (
                    <>
                        <p className="mb-2">
                            Att släppa nödstoppet låter aktiverade klasser åter bokföra <strong>utan granskning</strong>
                            {' '}upp till sina beloppstak.
                        </p>
                        <p className="settings-modal__note mb-0">
                            <AlertTriangle size={13} className="me-1" style={{ verticalAlign: -1 }} />
                            Kontrollera att klassinställningarna nedan stämmer innan du fortsätter.
                        </p>
                    </>
                )}
            </SettingsModal>
        </div>
    );
}
